import Dexie, { type Table, type Transaction } from 'dexie';
import type {
  AppSettings,
  CalibrationMeasurement,
  CloudSyncMetadata,
  Container,
  ConversionConfiguration,
  DosageTable,
  MeasuringCup,
  Product,
  RecommendationRule,
  RecoverySnapshot,
  SecurityConfig,
  StainGuide,
  TemperatureGuidance,
  WaterHardness,
} from '../domain/types';
import {
  DATABASE_SCHEMA_VERSION,
  SEED_CONTAINERS,
  SEED_CONVERSION_CONFIGURATIONS,
  SEED_DOSAGE_TABLES,
  SEED_MEASURING_CUPS,
  SEED_PRODUCTS,
  SEED_RECOMMENDATION_RULES,
  SEED_SETTINGS,
  SEED_STAIN_GUIDES,
  SEED_TEMPERATURE_GUIDANCE,
  SEED_WATER_HARDNESS,
} from './seed';

const VERSION_ONE_STORES = {
  settings: '&id',
  products: '&id, active, sortOrder',
  dosageTables:
    '&id, productId, waterHardnessId, soilLevelId, [productId+waterHardnessId+soilLevelId]',
  waterHardness: '&id, active, sortOrder',
  containers: '&id, active, isDefault, sortOrder',
  measuringCups: '&id, active, sortOrder',
};

const VERSION_TWO_STORES = {
  ...VERSION_ONE_STORES,
  conversionConfigurations: '&productId, activeConversionMode, calibrationStatus',
  calibrationMeasurements: '&id, runId, productId, cupId, measuredAt',
  security: '&id',
};

export const VERSION_THREE_STORES = {
  ...VERSION_TWO_STORES,
  temperatureGuidance: '&id, productId, active, sortOrder',
  recommendationRules: '&id, textileCategoryId, productId, active, priority',
  stainGuides: '&id, active, sortOrder',
  recoverySnapshots: '++id, createdAt, reason',
};

const VERSION_FOUR_STORES = {
  ...VERSION_THREE_STORES,
  cloudSyncMetadata: '&id',
};

async function migrateVersionTwo(transaction: Transaction): Promise<void> {
  const products = await transaction.table<Product, string>('products').toArray();
  const now = new Date().toISOString();
  await transaction.table<ConversionConfiguration, string>('conversionConfigurations').bulkPut(
    products.map((product) => ({
      productId: product.id,
      schoolGramsPerMl: 1,
      calibratedGramsPerMl: null,
      activeConversionMode: 'school',
      calibrationStatus: 'notCalibrated',
      calibrationDate: null,
      calibrationNote: '',
      updatedAt: now,
    })),
  );
}

async function migrateVersionThree(transaction: Transaction): Promise<void> {
  const productIds = new Set(
    (await transaction.table<Product, string>('products').toArray()).map(({ id }) => id),
  );
  await transaction
    .table<TemperatureGuidance, string>('temperatureGuidance')
    .bulkAdd(
      structuredClone(SEED_TEMPERATURE_GUIDANCE.filter((entry) => productIds.has(entry.productId))),
    );
  await transaction
    .table<RecommendationRule, string>('recommendationRules')
    .bulkAdd(
      structuredClone(
        SEED_RECOMMENDATION_RULES.filter(
          (entry) => entry.productId === null || productIds.has(entry.productId),
        ),
      ),
    );
  await transaction
    .table<StainGuide, string>('stainGuides')
    .bulkAdd(structuredClone([...SEED_STAIN_GUIDES]));
}

export class WaschPilotDatabase extends Dexie {
  settings!: Table<AppSettings, 'app'>;
  products!: Table<Product, string>;
  dosageTables!: Table<DosageTable, string>;
  waterHardness!: Table<WaterHardness, string>;
  containers!: Table<Container, string>;
  measuringCups!: Table<MeasuringCup, string>;
  conversionConfigurations!: Table<ConversionConfiguration, string>;
  calibrationMeasurements!: Table<CalibrationMeasurement, string>;
  temperatureGuidance!: Table<TemperatureGuidance, string>;
  recommendationRules!: Table<RecommendationRule, string>;
  stainGuides!: Table<StainGuide, string>;
  security!: Table<SecurityConfig, 'teacher-pin'>;
  recoverySnapshots!: Table<RecoverySnapshot, number>;
  cloudSyncMetadata!: Table<CloudSyncMetadata, 'school-config'>;

  constructor(name = 'waschpilot-schule') {
    super(name);

    this.version(1).stores(VERSION_ONE_STORES);
    this.version(2).stores(VERSION_TWO_STORES).upgrade(migrateVersionTwo);
    this.version(3).stores(VERSION_THREE_STORES).upgrade(migrateVersionThree);
    this.version(DATABASE_SCHEMA_VERSION).stores(VERSION_FOUR_STORES);

    this.on('populate', () => populateDefaultData(this));
  }
}

/** Populates a brand-new database. Existing user edits are never overwritten. */
export async function populateDefaultData(database: WaschPilotDatabase): Promise<void> {
  await database.settings.add({ ...SEED_SETTINGS });
  await database.products.bulkAdd(structuredClone([...SEED_PRODUCTS]));
  await database.dosageTables.bulkAdd(structuredClone([...SEED_DOSAGE_TABLES]));
  await database.waterHardness.bulkAdd(structuredClone([...SEED_WATER_HARDNESS]));
  await database.containers.bulkAdd(structuredClone([...SEED_CONTAINERS]));
  await database.measuringCups.bulkAdd(structuredClone([...SEED_MEASURING_CUPS]));
  await database.conversionConfigurations.bulkAdd(
    structuredClone([...SEED_CONVERSION_CONFIGURATIONS]),
  );
  await database.temperatureGuidance.bulkAdd(structuredClone([...SEED_TEMPERATURE_GUIDANCE]));
  await database.recommendationRules.bulkAdd(structuredClone([...SEED_RECOMMENDATION_RULES]));
  await database.stainGuides.bulkAdd(structuredClone([...SEED_STAIN_GUIDES]));
}

export const db = new WaschPilotDatabase();

export async function initializeDatabase(database: WaschPilotDatabase = db): Promise<void> {
  if (!database.isOpen()) {
    await database.open();
  }
}
